import { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import LdsIcon from '../LdsIcon/index.jsx';

function LdsStepIndicator({
  id,
  activeIndex = 0,
  checkIconLabel = 'Complete',
  stepIndicatorStep = 'Step',
  stepIndicatorOf = 'of',
  steps,
  className,
  ...rest
}) {
  const indicatorRef = useRef(null);
  const [condensed, setCondensed] = useState(false);
  const [stepsArray, setStepsArray] = useState([]);
  const [percentComplete, setPercentComplete] = useState(0);

  const stepCount = typeof steps === 'number' ? steps : steps.length;

  const checkWidth = () => {
    const minStepWidth = (stepCount * 32) + ((stepCount - 1) * 32);
    const indicatorWidth = indicatorRef.current.getBoundingClientRect().width;
    const isCondensed = minStepWidth > indicatorWidth;
    setCondensed(isCondensed);
  };

  // The "bubbles" don't "expand" so they should contain no more than two string characters
  // The "bubbles" become "dots" in the narrow/mobile responsive transition
  useEffect(() => {
    checkWidth();

    window.addEventListener('resize', checkWidth);

    return () => {
      window.removeEventListener('resize', checkWidth);
    };
  }, [indicatorRef]);

  useEffect(() => {
    const stepType = typeof steps;
    const newSteps = stepType === 'number' ? Array.from(Array(steps).keys(), (_, i) => i + 1) : steps;
    const percentage = (activeIndex / (newSteps.length - 1)) * 100;
    setStepsArray(newSteps);
    setPercentComplete(percentage < 100 ? percentage : 100);
  }, [steps, activeIndex]);

  return (
    <div
      role="region"
      id={id}
      ref={indicatorRef}
      className={cn(
        'lds-step-indicator',
        {
          condensed,
        },
        className
      )}
      aria-live="polite"
      aria-label={`${stepIndicatorStep} ${activeIndex + 1} ${stepIndicatorOf} ${stepCount}`}
      {...rest}
    >
      <div className="lds-step-indicator-lines">
        <div className="lds-step-indicator-line-default" />
        <div
          className="lds-step-indicator-line-complete"
          style={{ width: `${percentComplete}%` }}
        />
      </div>
      <ol role="list">
        {
          stepsArray.map((step, index) => (
            <li
              id={`step-indicator-dots-${index + 1}`}
              key={`step-indicator-dots-${index}`}
              className={cn(
                {
                  active: index === activeIndex,
                  completed: index < activeIndex,
                }
              )}
              aria-current={ (index === activeIndex) ? `${stepIndicatorStep}` : null}
            >
              <span className={ (index < activeIndex) ? 'sr-only' : null}>
                {step}
              </span>
              {
                (index < activeIndex)
                && <LdsIcon name="Check" label={checkIconLabel} className="lds-step-indicator-checkmark" inline/>
              }
            </li>
          ))
        }
      </ol>
      <div className="lds-step-indicator-step-count">
        <span
          className="sr-only"
        >
          {`${stepIndicatorStep}`}
        </span>
        {`${activeIndex + 1 } ${stepIndicatorOf} ${stepCount}`}
      </div>
    </div>
  );
}

export default LdsStepIndicator;

LdsStepIndicator.propTypes = {
  activeIndex: PropTypes.number,
  id: PropTypes.string,
  checkIconLabel: PropTypes.string,
  stepIndicatorStep: PropTypes.string,
  stepIndicatorOf: PropTypes.string,
  steps: PropTypes.any.isRequired,
};
